/**
 * workspace-datetime — the workspace's own date and time format, applied to
 * every screen (item 8, CC 0079 / 0084 / 0085).
 *
 * Settings > General holds `dateFormat` (a pattern such as "DD/MM/YYYY") and
 * `timeFormat` ("24h" | "12h"). Once applied, `lib/datetime`'s friendly* helpers
 * and `friendlyClock` print dates and times in them, so a journey no longer
 * mixes "Sep 28, 2026, 16:00" with "Ends at 4:50pm".
 *
 * - Studio reads them from the same public theme read as the public pages
 *   (below), never from getGeneralSettings. That read needs settings.read, which a
 *   team member does not hold, so it was refused on every Studio load for anyone
 *   but an administrator and left them on the locale defaults. The theme read
 *   serves both formats by the server's own rule (the stored pattern when it is
 *   one the pages use; the clock as set, else the workspace language's default),
 *   so an administrator sees what Settings > General would have given. A failed
 *   read leaves the locale defaults.
 * - Public pages (booking, events, walk-in, One-Stop, manage) read them from the
 *   public theme read, getPublicBrandingTheme (fields at the top level, beside
 *   `theme`; CC is building the server side). Each public page makes that read
 *   ONCE at start-up (CC 0085, option a). While the fields are absent, the page
 *   keeps its locale's format.
 *
 * A pattern that isn't made of the Settings tokens is ignored (isDatePattern),
 * because the contract type is a free string and the generated mock serves a
 * placeholder.
 */
import { getPublicBrandingTheme } from "@v0y4ge/api-client";
import { readPublicBrandingTheme } from "./public-branding-theme";
import { configureDateTime, setWorkspaceDatePattern, setWorkspaceWeekStart } from "./datetime";
import {
  getConfirmedTenantSlug, getTenantGeneration, isSignedIn,
  onTenantChange, onTenantIdentityResolved, onTenantSwitching, subscribeAccess,
} from "../shell/access";

export function applyWorkspaceDateTime(s: { dateFormat?: unknown; timeFormat?: unknown; defaultLanguage?: unknown; weekStartsOn?: unknown } | null | undefined): void {
  /* A successful theme response with no saved preference means the product default: Monday. */
  setWorkspaceWeekStart(s?.weekStartsOn);
  if (!s) return;
  setWorkspaceDatePattern(s.dateFormat);
  /* An unset clock follows the workspace language, by the same rule the server
     uses for the public read and the emails (CC 0081 §3-4, corrected in 0092):
     so Studio and the public pages never disagree. */
  const tf = s.timeFormat === "12h" || s.timeFormat === "24h" ? s.timeFormat
    : "defaultLanguage" in s ? (is24hLanguage(s.defaultLanguage) ? "24h" : "12h")
    : null; // a read with no clock and no language (the public theme read): keep the locale's clock
  if (tf === "12h") configureDateTime({ hour12: true });
  else if (tf === "24h") configureDateTime({ hour12: false });
}

/** The server's rule, exactly (CC 0092 §2a): 24h ONLY for English (UK), French,
 *  German, Spanish and Japanese; 12h for everything else, an unset language
 *  included. Settings stores the display name; a locale tag is accepted too. */
const LANGUAGES_24H = new Set(["english (uk)", "en-gb", "french", "fr", "fr-fr", "german", "de", "de-de", "spanish", "es", "es-es", "japanese", "ja", "ja-jp"]);
function is24hLanguage(lang: unknown): boolean {
  return typeof lang === "string" && LANGUAGES_24H.has(lang.trim().toLowerCase().replace(/_/g, "-"));
}

export interface StudioThemeWeekStartRead { ok: boolean; weekStartsOn: "monday" | "sunday" | "saturday"; }
const STALE_STUDIO_THEME = Symbol("stale studio theme read");
let studioEpoch = 0;
let studioOwner: { slug: string; generation: number } | null = null;
let studioRead: Promise<StudioThemeWeekStartRead> | null = null;
let studioReadPending = false;
function clearStudioWeekStart(): void {
  studioEpoch++;
  studioOwner = null;
  studioRead = null;
  studioReadPending = false;
  setWorkspaceWeekStart(undefined);
  setWorkspaceDatePattern(undefined);
  configureDateTime({ hour12: undefined });
}
function currentStudioIdentity(): { slug: string; generation: number } | null {
  const slug = getConfirmedTenantSlug();
  return isSignedIn() && slug ? { slug, generation: getTenantGeneration() } : null;
}
let previousSignedIn = isSignedIn();
onTenantSwitching(clearStudioWeekStart);
onTenantChange(() => {
  clearStudioWeekStart();
  if (currentStudioIdentity()) void loadStudioWorkspaceDateTime();
});
onTenantIdentityResolved(() => {
  clearStudioWeekStart();
  if (currentStudioIdentity()) void loadStudioWorkspaceDateTime();
});
subscribeAccess(() => {
  const signedIn = isSignedIn();
  if (previousSignedIn && !signedIn) clearStudioWeekStart();
  else if (!previousSignedIn && signedIn && currentStudioIdentity()) {
    clearStudioWeekStart();
    void loadStudioWorkspaceDateTime();
  }
  previousSignedIn = signedIn;
});

function readStudioThemeWeekStart(refresh = false): Promise<StudioThemeWeekStartRead> {
  const identity = currentStudioIdentity();
  if (!identity) return Promise.resolve({ ok: false, weekStartsOn: "monday" });
  /* A forced refresh may bypass a settled value, never a request still in flight:
     otherwise the older response could overwrite the newer tenant format/week. */
  if (studioRead && studioReadPending) return studioRead;
  if (studioRead && !refresh && studioOwner?.slug === identity.slug && studioOwner.generation === identity.generation) return studioRead;
  if (studioRead && !refresh) return studioRead;
  const epoch = studioEpoch;
  const read = Promise.resolve().then(() => {
    if (epoch !== studioEpoch || !currentStudioIdentity()
      || currentStudioIdentity()?.slug !== identity.slug || getTenantGeneration() !== identity.generation) return STALE_STUDIO_THEME;
    const baseUrl = (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "";
    return getPublicBrandingTheme({ baseUrl } as never) as Promise<unknown>;
  }).then((raw): StudioThemeWeekStartRead => {
    if (raw === STALE_STUDIO_THEME || epoch !== studioEpoch || !currentStudioIdentity()
      || getConfirmedTenantSlug() !== identity.slug || getTenantGeneration() !== identity.generation) {
      return { ok: false, weekStartsOn: "monday" };
    }
    const result = raw as { dateFormat?: unknown; timeFormat?: unknown; defaultLanguage?: unknown; weekStartsOn?: unknown } | null;
    applyWorkspaceDateTime(result);
    const pref = result?.weekStartsOn;
    const weekStartsOn = pref === "sunday" || pref === "saturday" ? pref : "monday";
    studioOwner = identity;
    return { ok: true, weekStartsOn };
  }).catch(() => ({ ok: false, weekStartsOn: "monday" as const }))
    .then((result) => {
      if (studioRead === read) {
        studioReadPending = false;
        if (epoch === studioEpoch && !result.ok) studioRead = null;
      }
      return result;
    });
  studioRead = read;
  studioReadPending = true;
  return read;
}
/** TEST SEAM: clear session-owned workspace values between unit cases. */
export function resetStudioWorkspaceDateTimeForTests(): void {
  clearStudioWeekStart();
  previousSignedIn = isSignedIn();
}
/** Studio: one shared read per confirmed signed-in tenant session. */
export function loadStudioWorkspaceDateTime(): Promise<void> {
  return readStudioThemeWeekStart(false).then(() => undefined);
}
/** My Calendar's late member preference / background recheck. Failures retain the last known value. */
export function readStudioWorkspaceWeekStart(refresh = false): Promise<StudioThemeWeekStartRead> {
  return readStudioThemeWeekStart(refresh);
}

let publicRead: Promise<void> | null = null;
/** Public pages: once at start-up. */
export function loadPublicWorkspaceDateTime(): Promise<void> {
  if (publicRead) return publicRead;
  /* The same read the branded chrome uses (CC 0315): one request per page. */
  publicRead = readPublicBrandingTheme()
    .then((r) => { applyWorkspaceDateTime((r ?? null) as { dateFormat?: unknown; timeFormat?: unknown; weekStartsOn?: unknown } | null); })
    .catch(() => { /* the theme read failed: locale defaults stay */ });
  return publicRead;
}
